<div class="page-container">
  <app-card>
    <app-card-header>
      <app-card-title>
        <div class="flex gap-md flex-align-center flex-justify-end flex-wrap">
          @if (checkPoint() && form()) {
            <app-checkpoint-form-header
              class="mr-auto"
              [checkpointName]="checkPoint()!.name"
              [formName]="form()!.name"
              [serialNumber]="$safeNavigationMigration(instance()?.serial_number)"
            />
            @if (form()!.form_definition_id) {
              <div class="form-actions flex gap-md flex-align-center flex-justify-end flex-wrap">
                <app-button
                  variant="outlined"
                  type="button"
                  (click)="fillOutForm()"
                  data-testid="fill-out-form-button"
                  *hasPermission="'submission.create'"
                >
                  <app-icon name="file-plus" />
                  {{ 'component.qr.fillOutForm' | translate }}
                </app-button>
                <app-button
                  *hasPermission="'form.update'"
                  variant="outlined"
                  type="button"
                  (click)="editForm()"
                  data-testid="edit-form-button"
                >
                  <app-icon name="settings" />
                  {{ 'component.qr.editForm' | translate }}
                </app-button>
              </div>
            }
          }
        </div>
      </app-card-title>
    </app-card-header>

    <app-card-content>
      <div class="flex-column height-full">
        @if (loading()) {
          <div class="flex flex-column flex-align-center gap-lg p-3xl">
            <app-spinner></app-spinner>
            <p class="m-0 fg-secondary">{{ 'submission.history.loading' | translate }}</p>
          </div>
        } @else if (error()) {
          <div class="flex flex-align-center gap-md p-lg bg-danger fg-danger radius-md">
            <app-icon name="alert-circle" />
            <p class="m-0">{{ error() }}</p>
          </div>
        } @else if (filteredRecords().length === 0 && !searchTerm()) {
          <div class="flex flex-column flex-align-center gap-lg p-3xl text-center">
            <app-icon name="inbox" [size]="48" />
            <h3 class="m-0 font-xl fg-primary">
              {{ 'submission.history.empty.title' | translate }}
            </h3>
            <p class="m-0 fg-secondary">{{ 'submission.history.empty.message' | translate }}</p>
          </div>
        } @else {
          <!-- Controls row - always visible when there are records -->
          @if (records().length > 0) {
            <div class="flex flex-justify-space-between flex-align-center gap-md mb-md">
              @if (legacyFields().length > 0) {
                <app-checkbox
                  [(ngModel)]="showLegacyFieldsValue"
                  (ngModelChange)="showLegacyFields.set($event)"
                  data-testid="show-legacy-fields-toggle"
                >
                  {{
                    'submission.history.showLegacy'
                      | translate
                        : {
                            count: legacyFields().length.toString(),
                            plural: legacyFields().length === 1 ? '' : 's',
                          }
                  }}
                </app-checkbox>
              } @else {
                <div></div>
              }
              <div class="flex gap-sm flex-align-center">
                <app-input
                  [leadingIcon]="'search'"
                  [trailingIcon]="'x'"
                  [trailingIconHidden]="!searchTerm()"
                  (trailingIconClick)="clearSearch()"
                >
                  <input
                    type="text"
                    [placeholder]="'submission.history.search' | translate"
                    [(ngModel)]="searchTermValue"
                    (input)="onSearchInput($event)"
                    data-testid="interaction-search-input"
                  />
                </app-input>
                <app-button
                  variant="icon"
                  type="button"
                  (click)="openDownloadDialog()"
                  [tippy]="'submission.download.tooltip' | translate"
                  data-testid="download-button"
                >
                  <app-icon name="download" />
                </app-button>
              </div>
            </div>
          }

          <!-- Empty search results message -->
          @if (filteredRecords().length === 0 && searchTerm()) {
            <div class="flex flex-column flex-align-center gap-lg p-3xl text-center">
              <app-icon name="search" [size]="48" />
              <h3 class="m-0 font-xl fg-primary">
                {{ 'submission.history.noResults.title' | translate }}
              </h3>
              <p class="m-0 fg-secondary">
                {{ 'submission.history.noResults.message' | translate }}
              </p>
            </div>
          }

          <!-- Table with results -->
          @if (filteredRecords().length > 0) {
            <div class="table-container flex-grow">
              <table class="records-table">
                <thead>
                  <tr>
                    <th>{{ 'submission.history.dateTime' | translate }}</th>
                    @for (column of columns(); track column) {
                      <th>{{ getColumnLabel(column) }}</th>
                    }
                    <th class="actions-column">{{ 'submission.history.actions' | translate }}</th>
                  </tr>
                </thead>
                <tbody class="overflow-y-auto">
                  @for (record of filteredRecords(); track record.id) {
                    <tr>
                      <td class="timestamp-cell">
                        <div class="timestamp-date">{{ formatDate(record.created_at).date }}</div>
                        <div class="timestamp-time">{{ formatDate(record.created_at).time }}</div>
                      </td>
                      @for (column of columns(); track column) {
                        <td>
                          @if (isPhotoField(column) && getCellValue(record, column)) {
                            <app-photo-thumbnail
                              [photos]="getPhotoUrls(record, column)"
                              [attr.data-testid]="'photo-thumbnail-cell-' + column"
                            >
                            </app-photo-thumbnail>
                          } @else {
                            {{ getCellValue(record, column) }}
                          }
                        </td>
                      }
                      <td class="actions-cell">
                        <div class="actions-container flex gap-sm">
                          <app-button
                            variant="icon"
                            type="button"
                            (click)="viewRecord(record.id, $event)"
                            [attr.data-testid]="'view-record-' + record.id"
                          >
                            <app-icon name="eye" />
                          </app-button>
                          <app-button
                            variant="icon"
                            type="button"
                            (click)="showDeleteConfirmation(record.id, $event)"
                            [attr.data-testid]="'delete-record-' + record.id"
                          >
                            <app-icon name="trash-2" />
                          </app-button>
                        </div>
                      </td>
                    </tr>
                  }
                </tbody>
              </table>
            </div>
          }
        }
      </div>
    </app-card-content>
  </app-card>

  @if (showDeleteModal() && recordToDelete()) {
    <app-modal-dialog (backdropClick)="cancelDelete()">
      <div header>
        <div class="modal-title-row">
          <div class="heading-md">{{ 'submission.history.deleteModal.title' | translate }}</div>
          <app-button variant="icon" (click)="cancelDelete()">
            <app-icon name="x" />
          </app-button>
        </div>
      </div>

      <div content>
        <p>{{ 'submission.history.deleteModal.message1' | translate }}</p>
        <p>{{ 'submission.history.deleteModal.message2' | translate }}</p>
      </div>

      <div footer>
        <app-button variant="text" (click)="cancelDelete()">
          {{ 'submission.history.deleteModal.cancel' | translate }}
        </app-button>
        <app-button color="danger" (click)="confirmDelete()">
          {{ 'submission.history.deleteModal.confirm' | translate }}
        </app-button>
      </div>
    </app-modal-dialog>
  }

  <!-- Download Dialog -->
  @if (showDownloadDialog()) {
    <app-modal-dialog (backdropClick)="closeDownloadDialog()">
      <div header>
        <div class="modal-title-row">
          <div class="heading-md">{{ 'submission.download.title' | translate }}</div>
          <app-button
            variant="icon"
            (click)="closeDownloadDialog()"
            data-testid="close-download-dialog"
          >
            <app-icon name="x" />
          </app-button>
        </div>
      </div>

      <div content>
        <app-download-dialog
          #downloadDialog
          [filteredCount]="filteredRecords().length"
          [totalCount]="records().length"
          (download)="handleDownload($event)"
          (cancel)="closeDownloadDialog()"
        />
      </div>

      <div footer class="flex flex-justify-end gap-md">
        <app-button
          variant="outlined"
          type="button"
          (click)="closeDownloadDialog()"
          data-testid="cancel-button"
        >
          {{ 'button.cancel' | translate }}
        </app-button>
        <app-button
          variant="filled"
          type="button"
          (click)="downloadDialog.onDownload()"
          data-testid="download-button"
        >
          {{ 'submission.download.button' | translate }}
        </app-button>
      </div>
    </app-modal-dialog>
  }
</div>
